'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronDown } from 'lucide-react';
import { trackEvent } from '@/components/GoogleAnalytics';
import { PUBLIC_SEARCH_AGE_GROUPS, publicSearchPath } from '@/utils/publicSearch';

const APP_BAR_DISMISSED_KEY = 'mvvs_app_notification_dismissed';
const AGE_OPTIONS = PUBLIC_SEARCH_AGE_GROUPS;

type LookingFor = 'bride' | 'groom';

export type HomeHeroProps = {
  title?: string;
  subtitle?: string;
  trackingSource?: string;
};

export default function HomeHero({
  title = 'मराठी वधू-वरांसाठी',
  subtitle = 'महाराष्ट्रातील अग्रणी विवाहसंस्था',
  trackingSource = 'homepage_hero',
}: HomeHeroProps) {
  const router = useRouter();
  const [lookingFor, setLookingFor] = useState<LookingFor>('bride');
  const [ageIndex, setAgeIndex] = useState(1);
  const [ageOpen, setAgeOpen] = useState(false);
  const ageRef = useRef<HTMLDivElement>(null);
  const ageListId = useId();
  const age = AGE_OPTIONS[ageIndex];
  const [appBarDismissed, setAppBarDismissed] = useState(false);

  useEffect(() => {
    const syncAppBar = () => {
      setAppBarDismissed(sessionStorage.getItem(APP_BAR_DISMISSED_KEY) === 'true');
    };

    syncAppBar();
    window.addEventListener('mvvs-app-bar-dismissed', syncAppBar);
    return () => window.removeEventListener('mvvs-app-bar-dismissed', syncAppBar);
  }, []);

  useEffect(() => {
    if (!ageOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!ageRef.current?.contains(event.target as Node)) {
        setAgeOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAgeOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [ageOpen]);

  const handleSearch = () => {
    const gender = lookingFor === 'bride' ? 'female' : 'male';
    trackEvent('click_hero_search', trackingSource, `${lookingFor} ${age.label}`);
    router.push(publicSearchPath(gender, age.ageGroup));
  };

  return (
    <section className="relative flex h-[100vh] items-end overflow-hidden">
      <link
        rel="preload"
        as="image"
        href="/assets/img/home-hero-828.webp"
        type="image/webp"
        media="(max-width: 767px)"
        fetchPriority="high"
      />
      <link
        rel="preload"
        as="image"
        href="/assets/img/home-hero-1280.webp"
        type="image/webp"
        media="(min-width: 768px) and (max-width: 1279px)"
        fetchPriority="high"
      />
      <link
        rel="preload"
        as="image"
        href="/assets/img/home-hero.webp"
        type="image/webp"
        media="(min-width: 1280px)"
        fetchPriority="high"
      />
      <div className="absolute inset-0">
        <picture className="block h-full w-full">
          <source media="(max-width: 767px)" srcSet="/assets/img/home-hero-828.webp" type="image/webp" />
          <source media="(max-width: 1279px)" srcSet="/assets/img/home-hero-1280.webp" type="image/webp" />
          <source srcSet="/assets/img/home-hero.webp" type="image/webp" />
          <img
            src="/assets/img/home-hero.jpg"
            alt="Marathi bride in traditional wedding attire"
            width={1890}
            height={1260}
            className="object-cover object-[44%_center] md:object-center"
            style={{
              position: 'absolute',
              inset: 0,
              height: '100%',
              width: '100%',
              maxWidth: 'none',
              objectFit: 'cover',
            }}
            decoding="sync"
            loading="eager"
            fetchPriority="high"
          />
        </picture>
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[72%] bg-gradient-to-b from-transparent via-black/45 to-black/85 md:h-[50%] md:via-black/25 md:to-black/75"
      />

      <div
        className={`relative z-10 flex w-full flex-col items-center gap-5 px-5 pt-10 md:pb-10 ${
          appBarDismissed
            ? 'pb-[calc(104px+env(safe-area-inset-bottom,0px))]'
            : 'pb-[calc(174px+env(safe-area-inset-bottom,0px))]'
        }`}
      >
        <div className="rounded-[20px] border border-black/50 bg-black/40 px-[15px] py-[5px]">
          <p className="text-center text-[12px] font-semibold tracking-[0.36px] text-white">
            ✦ Trusted since 1990 · 10,000+ verified profiles
          </p>
        </div>

        <h1 className="flex w-full max-w-[801px] flex-col items-center gap-6 text-center text-white">
          <span className="block text-[32px] font-bold leading-[1.2] tracking-[0.32px] min-[380px]:text-[36px] min-[380px]:tracking-[0.36px] md:text-[60px] md:leading-none md:tracking-[0.6px]">
            {title}
          </span>
          <span className="block text-[18px] font-medium leading-[1.2] tracking-[0.18px] md:text-[24px] md:leading-none md:tracking-[0.24px]">
            {subtitle}
          </span>
        </h1>

        <div className="mt-5 hidden items-center justify-center gap-3 rounded-[50px] bg-white/30 p-[12px] md:flex">
          <div
            className="flex items-center rounded-[34px] bg-[#fce8f1] p-1.5"
            role="radiogroup"
            aria-label="Looking for"
          >
            <button
              type="button"
              role="radio"
              aria-checked={lookingFor === 'bride'}
              onClick={() => setLookingFor('bride')}
              className={`flex items-center gap-2.5 rounded-full px-6 py-3 text-sm font-bold tracking-[0.42px] text-[#232323] ${
                lookingFor === 'bride' ? 'bg-white' : ''
              }`}
            >
              {lookingFor === 'bride' && (
                <span className="flex size-5 items-center justify-center rounded-full border-2 border-primary-600" aria-hidden>
                  <span className="size-2.5 rounded-full bg-primary-600" />
                </span>
              )}
              वधू (Bride)
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={lookingFor === 'groom'}
              onClick={() => setLookingFor('groom')}
              className={`flex items-center gap-2.5 rounded-full px-6 py-3 text-sm font-bold tracking-[0.42px] text-[#232323] ${
                lookingFor === 'groom' ? 'bg-white' : ''
              }`}
            >
              {lookingFor === 'groom' && (
                <span className="flex size-5 items-center justify-center rounded-full border-2 border-primary-600" aria-hidden>
                  <span className="size-2.5 rounded-full bg-primary-600" />
                </span>
              )}
              वर (Groom)
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative" ref={ageRef}>
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={ageOpen}
                aria-controls={ageListId}
                onClick={() => setAgeOpen((open) => !open)}
                className="flex items-center gap-2.5 rounded-[30px] border border-[#dfdfdf] bg-white px-[25px] py-3 text-sm font-medium tracking-[0.42px] text-[#232323]"
              >
                {age.label}
                <ChevronDown
                  size={20}
                  aria-hidden
                  className={`shrink-0 transition-transform ${ageOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {ageOpen && (
                <ul
                  id={ageListId}
                  role="listbox"
                  aria-label="Age range"
                  className="absolute bottom-full left-0 z-20 mb-2 min-w-full overflow-hidden rounded-2xl border border-[#dfdfdf] bg-white py-1 shadow-lg"
                >
                  {AGE_OPTIONS.map((option, index) => (
                    <li key={option.label} role="option" aria-selected={index === ageIndex}>
                      <button
                        type="button"
                        className={`w-full whitespace-nowrap px-4 py-2.5 text-left text-xs ${
                          index === ageIndex
                            ? 'bg-[#fce8f1] font-semibold text-[#232323]'
                            : 'text-[#232323] hover:bg-gray-50'
                        }`}
                        onClick={() => {
                          setAgeIndex(index);
                          setAgeOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              type="button"
              onClick={handleSearch}
              className="rounded-full bg-primary-600 px-[30px] py-3 text-base font-bold tracking-[0.48px] text-white transition-colors hover:bg-primary-700"
            >
              Search Now
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
